import React, { useEffect, useState } from "react";
import { api, streamSSE } from "@/lib/api";
import Markdown from "@/components/Markdown";
import { PREDICATE } from "@/constants/testIds";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import {
  Search, Sparkles, Loader2, Scale, TrendingUp,
  ShieldAlert, Bookmark, BookmarkPlus, Trash2, FolderOpen, X, Info, Lock, Wand2,
} from "lucide-react";

function ScoreBadge({ score, matched, breakdown, semanticScore, semanticReason }) {
  const s = Math.max(0, Math.min(100, Math.round(score || 0)));
  const [open, setOpen] = useState(false);
  const tier =
    s >= 70 ? { bg: "bg-emerald-500/15", border: "border-emerald-400/40", text: "text-emerald-300" }
    : s >= 40 ? { bg: "bg-amber-500/15",  border: "border-amber-400/40",  text: "text-amber-300" }
    :          { bg: "bg-slate-50",        border: "border-slate-200",     text: "text-slate-500" };

  const rows = breakdown ? [
    { k: "Device name overlap", v: `${Math.round(breakdown.device_overlap*100)}%`, w: "×0.45" },
    { k: "Exact phrase in name", v: breakdown.phrase_hit ? "yes" : "no",           w: "×0.25" },
    { k: "Statement overlap",   v: `${Math.round(breakdown.stmt_overlap*100)}%`,   w: "×0.15" },
    { k: "Applicant overlap",   v: `${Math.round(breakdown.applicant_overlap*100)}%`, w: "×0.10" },
    { k: "Product code match",  v: breakdown.pcode_match ? "yes" : "no",           w: "×0.05" },
    { k: "K-number exact",      v: breakdown.knum_match ? "yes" : "no",            w: "override" },
  ] : null;

  return (
    <div className="relative flex flex-col items-start gap-1" data-testid="predicate-match-cell">
      <button
        type="button"
        data-testid="predicate-match-badge"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        className={`inline-flex items-center gap-1 border ${tier.border} ${tier.bg} px-2 py-0.5 font-mono text-xs font-semibold ${tier.text} cursor-help`}
        title={`Click to see why · ${s}/100`}
      >
        <TrendingUp className="h-3 w-3" strokeWidth={2.5} /> {s}
        {typeof semanticScore === "number" && (
          <span className="ml-1 border-l border-slate-200 pl-1 font-normal text-slate-600" title={semanticReason || "AI semantic score"}>
            AI {semanticScore}
          </span>
        )}
      </button>
      {matched && matched.length > 0 && (
        <div className="flex flex-wrap gap-1 max-w-[160px]">
          {matched.slice(0, 3).map((t) => (
            <span key={t} className="border border-slate-200 px-1 text-[9px] uppercase tracking-wider text-slate-400">
              {t}
            </span>
          ))}
        </div>
      )}
      {open && rows && (
        <div
          data-testid="predicate-score-popover"
          onClick={(e) => e.stopPropagation()}
          className="absolute left-0 top-8 z-30 w-72 border border-slate-200 bg-white p-3 text-xs shadow-xl"
        >
          <div className="overline mb-2 text-[#2563EB]">WHY THIS SCORE — {s}/100</div>
          <table className="w-full">
            <tbody>
              {rows.map((r) => (
                <tr key={r.k} className="border-b border-slate-100">
                  <td className="py-1 pr-2 text-slate-500">{r.k}</td>
                  <td className="py-1 text-right font-mono text-slate-700">{r.v}</td>
                  <td className="py-1 pl-2 text-right text-slate-400">{r.w}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {typeof semanticScore === "number" && semanticReason && (
            <div className="mt-3 border-t border-slate-200 pt-2 text-white/75">
              <div className="overline mb-1 text-slate-400">AI SEMANTIC · {semanticScore}/100</div>
              {semanticReason}
            </div>
          )}
          <button
            onClick={() => setOpen(false)}
            className="mt-2 text-[10px] uppercase tracking-wider text-slate-400 hover:text-slate-600"
          >
            close
          </button>
        </div>
      )}
    </div>
  );
}

function MaudeBadge({ radar, trend }) {
  if (!radar) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-300">
        <Loader2 className="h-3 w-3 animate-spin" /> checking
      </span>
    );
  }
  if (!radar.ok || radar.event_count === 0) {
    return (
      <span
        data-testid="predicate-maude-badge"
        className="inline-flex items-center gap-1 border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] uppercase tracking-wider text-slate-500"
      >
        <ShieldAlert className="h-3 w-3" /> no events
      </span>
    );
  }
  const n = radar.event_count;
  const tier =
    n >= 1000 ? { bd: "border-rose-400/50",   bg: "bg-rose-500/10",   tx: "text-rose-300" }
    : n >= 100  ? { bd: "border-amber-400/50",  bg: "bg-amber-500/10",  tx: "text-amber-300" }
    :             { bd: "border-slate-200",      bg: "bg-slate-50",       tx: "text-slate-600" };
  const problems = (radar.top_problems || []).slice(0, 2);

  // Compact 24-month sparkline
  let sparkline = null;
  if (trend && trend.counts && trend.counts.length) {
    const w = 90, h = 22;
    const max = Math.max(1, ...trend.counts);
    const stepX = w / (trend.counts.length - 1 || 1);
    const pts = trend.counts.map((c, i) => `${(i * stepX).toFixed(1)},${(h - (c / max) * h).toFixed(1)}`).join(" ");
    const last = trend.counts[trend.counts.length - 1] ?? 0;
    const first = trend.counts[0] ?? 0;
    const rising = last > first * 1.15;
    sparkline = (
      <svg width={w} height={h} className="mt-0.5" data-testid="predicate-maude-sparkline">
        <polyline
          fill="none"
          stroke={rising ? "#F87171" : tier.tx === "text-rose-300" ? "#FCA5A5" : tier.tx === "text-amber-300" ? "#FCD34D" : "#94A3B8"}
          strokeWidth="1.4"
          points={pts}
        />
      </svg>
    );
  }

  return (
    <div className="flex flex-col items-start gap-1" data-testid="predicate-maude-cell">
      <span
        data-testid="predicate-maude-badge"
        className={`inline-flex items-center gap-1 border ${tier.bd} ${tier.bg} px-2 py-0.5 font-mono text-xs font-semibold ${tier.tx}`}
        title={`${n.toLocaleString()} MAUDE reports${radar.latest_event_date ? ` · latest ${radar.latest_event_date}` : ""}${trend ? ` · 24-mo trend: ${trend.total.toLocaleString()}` : ""}`}
      >
        <ShieldAlert className="h-3 w-3" strokeWidth={2.5} />
        {n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : n}
      </span>
      {sparkline}
      {problems.length > 0 && (
        <div className="flex flex-wrap gap-1 max-w-[160px]">
          {problems.map((p) => (
            <span
              key={p.problem}
              className="border border-slate-200 px-1 text-[9px] uppercase tracking-wider text-slate-400"
              title={`${p.count} reports of "${p.problem}"`}
            >
              {p.problem?.slice(0, 22)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PredicateSearch() {
  const { user } = useAuth();
  const canSaveSets = user?.plan_limits?.saved_sets;
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [radar, setRadar] = useState({});
  const [trends, setTrends] = useState({});
  const [semantic, setSemantic] = useState({});
  const [rerankLoading, setRerankLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [subject, setSubject] = useState("");
  const [subjectDesc, setSubjectDesc] = useState("");
  const [analysis, setAnalysis] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [sets, setSets] = useState([]);
  const [savingSet, setSavingSet] = useState(false);
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [setName, setSetName] = useState("");

  const loadSets = async () => {
    try {
      const { data } = await api.get("/predicate-sets");
      setSets(data.items || []);
    } catch (e) {}
  };
  useEffect(() => { if (canSaveSets) loadSets(); }, [canSaveSets]);

  const fetchRadarAndTrend = async (hits) => {
    const top = hits.filter((h) => (h.match_score ?? 0) >= 40).slice(0, 15);
    if (top.length === 0) return;
    const devices = top.map((h) => ({ k_number: h.k_number, device_name: h.device_name || "" }));
    try {
      const [radarRes, trendRes] = await Promise.all([
        api.post("/predicate/maude-radar", { devices }),
        api.post("/predicate/maude-trend", { devices }),
      ]);
      setRadar((prev) => ({ ...prev, ...(radarRes.data.radar || {}) }));
      setTrends((prev) => ({ ...prev, ...(trendRes.data.trends || {}) }));
    } catch (e) {}
  };

  const runSemanticRerank = async () => {
    if (rows.length === 0) return;
    setRerankLoading(true);
    try {
      const top10 = rows.slice(0, 10);
      const { data } = await api.post("/predicate/semantic-rerank", { query: q, hits: top10 });
      const map = {};
      for (const r of (data.reranked || [])) map[r.k_number] = { score: r.semantic_score, reason: r.reason };
      setSemantic(map);
      toast.success(`Re-ranked top ${Object.keys(map).length} with AI`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rerank failed");
    } finally {
      setRerankLoading(false);
    }
  };

  const search = async () => {
    if (!q.trim()) return;
    setLoading(true); setRows([]); setSelected(new Set()); setRadar({}); setTrends({}); setSemantic({});
    try {
      const { data } = await api.get("/predicate/search", { params: { q, limit: 25 } });
      setRows(data.results || []);
      if (!data.results?.length) toast.info("No predicate devices found for that query");
      else fetchRadarAndTrend(data.results);
    } catch (e) {
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  };

  const loadSet = async (set) => {
    try {
      setLoading(true);
      const { data } = await api.get("/predicate/batch", { params: { ks: set.k_numbers.join(",") } });
      setRows(data.results || []);
      setSelected(new Set(set.k_numbers));
      setRadar({}); setTrends({}); setSemantic({});
      setQ(`Set: ${set.name}`);
      fetchRadarAndTrend(data.results || []);
      toast.success(`Loaded set "${set.name}" (${set.k_numbers.length} predicates)`);
    } catch (e) {
      toast.error("Could not load set");
    } finally {
      setLoading(false);
    }
  };

  const deleteSet = async (set) => {
    if (!confirm(`Delete saved set "${set.name}"?`)) return;
    try {
      await api.delete(`/predicate-sets/${set.id}`);
      setSets((prev) => prev.filter((s) => s.id !== set.id));
      toast.success("Set deleted");
    } catch (e) {
      toast.error("Could not delete");
    }
  };

  const saveCurrentSet = async () => {
    const name = setName.trim();
    if (!name) return toast.error("Give the set a name");
    if (selected.size === 0) return toast.error("Select at least one predicate first");
    setSavingSet(true);
    try {
      const { data } = await api.post("/predicate-sets", {
        name, k_numbers: Array.from(selected),
      });
      setSets((prev) => [data, ...prev]);
      setSetName(""); setShowSaveInput(false);
      toast.success(`Saved "${name}"`);
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error("Saved sets are a Pro feature", {
          action: { label: "Upgrade", onClick: () => window.location.href = "/pricing" },
        });
      } else {
        toast.error(e?.response?.data?.detail || "Could not save set");
      }
    } finally {
      setSavingSet(false);
    }
  };

  const toggle = (k) => {
    const s = new Set(selected);
    s.has(k) ? s.delete(k) : s.add(k);
    setSelected(s);
  };

  const runCompare = async () => {
    if (selected.size === 0) return toast.error("Select at least one predicate");
    if (!subject.trim() || !subjectDesc.trim()) return toast.error("Subject device name and description required");
    setAnalyzing(true); setAnalysis("");
    try {
      let buf = "";
      await streamSSE("/predicate/compare", {
        subject_device: subject, subject_description: subjectDesc,
        predicate_k_numbers: Array.from(selected),
      }, (frame) => {
        if (frame.delta) { buf += frame.delta; setAnalysis(buf); }
        if (frame.error) toast.error(frame.error);
      });
      toast.success("Comparison ready");
    } catch (e) {
      toast.error("Analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <div className="overline mb-2 text-slate-500">openFDA · 510(k) live</div>
        <h1 className="font-serif text-3xl font-semibold tracking-tight lg:text-4xl">
          Predicate device search.
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-500">
          Search the live FDA 510(k) database, save sets, and let AI draft a Substantial Equivalence
          comparison per 21 CFR 807.87(f). Each result is ranked, adverse-event-checked, and now
          AI-semantic-scorable on demand.
        </p>
      </div>

      {sets.length > 0 && (
        <div className="mb-5" data-testid="predicate-saved-sets">
          <div className="overline mb-2 flex items-center gap-2 text-slate-500">
            <FolderOpen className="h-3 w-3" /> SAVED SETS
          </div>
          <div className="flex flex-wrap gap-2">
            {sets.map((s) => (
              <div key={s.id} data-testid={`predicate-set-chip-${s.id}`}
                   className="group inline-flex items-center gap-2 border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 hover:border-[#2563EB]">
                <button onClick={() => loadSet(s)} data-testid="predicate-set-load" className="inline-flex items-center gap-2 font-medium">
                  <Bookmark className="h-3 w-3 text-[#93C5FD]" />
                  {s.name}<span className="text-slate-400">{s.k_numbers.length}</span>
                </button>
                <button onClick={() => deleteSet(s)} data-testid="predicate-set-delete"
                        className="text-slate-300 opacity-0 transition-opacity hover:text-rose-400 group-hover:opacity-100" title="Delete set">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mb-6 flex flex-wrap gap-3">
        <div className="flex flex-1 items-center border border-slate-200 bg-white pl-4">
          <Search className="h-4 w-4 text-slate-400" />
          <input data-testid={PREDICATE.searchInput} value={q} onChange={(e) => setQ(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && search()}
                 placeholder="e.g., pulse oximeter, insulin pump, K221234…"
                 className="flex-1 border-none bg-transparent px-3 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none" />
        </div>
        <button data-testid={PREDICATE.searchBtn} onClick={search} disabled={loading}
                className="inline-flex items-center gap-2 bg-[#2563EB] px-5 py-3 text-sm font-semibold text-white transition-all hover:bg-[#1D4ED8] disabled:opacity-50">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Search
        </button>
      </div>

      {rows.length > 0 && (
        <div className="mb-8 border border-slate-200">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-3">
            <div className="overline text-slate-500">
              {rows.length} MATCH{rows.length === 1 ? "" : "ES"} · RANKED BY SIMILARITY · MAUDE RADAR ACTIVE
            </div>
            <div className="flex items-center gap-3">
              <div className="overline text-[#2563EB]">{selected.size} SELECTED</div>
              <button
                onClick={runSemanticRerank}
                disabled={rerankLoading || rows.length === 0}
                data-testid="predicate-rerank-btn"
                className="inline-flex items-center gap-1.5 border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700 hover:border-[#2563EB] hover:text-slate-900 disabled:opacity-50"
                title="Ask Claude to semantically re-rank the top 10"
              >
                {rerankLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
                {Object.keys(semantic).length > 0 ? "Re-run AI rerank" : "Refine with AI"}
              </button>
              {selected.size > 0 && !showSaveInput && (
                canSaveSets ? (
                  <button onClick={() => setShowSaveInput(true)} data-testid="predicate-save-set-btn"
                          className="inline-flex items-center gap-1.5 border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700 hover:border-[#2563EB] hover:text-slate-900">
                    <BookmarkPlus className="h-3 w-3" /> Save set
                  </button>
                ) : (
                  <Link to="/pricing" data-testid="predicate-save-set-locked"
                        className="inline-flex items-center gap-1.5 border border-amber-400/40 bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-300 hover:border-amber-300">
                    <Lock className="h-3 w-3" /> Save set · Pro
                  </Link>
                )
              )}
            </div>
          </div>
          {showSaveInput && (
            <div className="flex items-center gap-2 border-b border-slate-200 bg-white/60 px-4 py-3">
              <input autoFocus data-testid="predicate-save-set-name" value={setName}
                     onChange={(e) => setSetName(e.target.value)}
                     onKeyDown={(e) => e.key === "Enter" && saveCurrentSet()}
                     placeholder="Name this set"
                     className="flex-1 border border-slate-200 bg-[#FAFAF7] px-3 py-1.5 text-xs text-slate-900 placeholder:text-slate-300 focus:border-[#2563EB] focus:outline-none" />
              <button onClick={saveCurrentSet} disabled={savingSet} data-testid="predicate-save-set-confirm"
                      className="inline-flex items-center gap-1.5 bg-[#2563EB] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#1D4ED8] disabled:opacity-50">
                {savingSet ? <Loader2 className="h-3 w-3 animate-spin" /> : <BookmarkPlus className="h-3 w-3" />}
                Save
              </button>
              <button onClick={() => { setShowSaveInput(false); setSetName(""); }}
                      className="border border-slate-200 px-3 py-1.5 text-xs text-slate-500 hover:text-slate-900">
                Cancel
              </button>
            </div>
          )}
          <div className="max-h-[420px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50">
                <tr className="border-b border-slate-200">
                  <th className="w-10 px-4 py-3"></th>
                  <th className="overline w-32 px-3 py-3 text-left">Match</th>
                  <th className="overline w-32 px-3 py-3 text-left">MAUDE · 24mo</th>
                  <th className="overline px-3 py-3 text-left">K Number</th>
                  <th className="overline px-3 py-3 text-left">Device</th>
                  <th className="overline px-3 py-3 text-left">Applicant</th>
                  <th className="overline px-3 py-3 text-left">Product Code</th>
                  <th className="overline px-3 py-3 text-left">Decision</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const isSel = selected.has(r.k_number);
                  const rad = radar[r.k_number];
                  const trend = trends[r.k_number];
                  const sem = semantic[r.k_number];
                  return (
                    <tr key={r.k_number} data-testid={PREDICATE.resultRow}
                        onClick={() => toggle(r.k_number)}
                        className={`cursor-pointer border-b border-slate-100 transition-colors ${isSel ? "bg-[#2563EB]/10" : "hover:bg-slate-50"}`}>
                      <td className="px-4 py-3">
                        <input type="checkbox" checked={isSel} onChange={() => toggle(r.k_number)}
                               className="h-3.5 w-3.5 accent-[#2563EB]" />
                      </td>
                      <td className="px-3 py-3">
                        <ScoreBadge score={r.match_score} matched={r.matched_terms} breakdown={r.score_breakdown}
                                    semanticScore={sem?.score} semanticReason={sem?.reason} />
                      </td>
                      <td className="px-3 py-3">
                        <MaudeBadge radar={rad} trend={trend} />
                      </td>
                      <td className="px-3 py-3 font-mono text-xs text-[#93C5FD]">{r.k_number}</td>
                      <td className="max-w-xs px-3 py-3 text-slate-800">{r.device_name || "–"}</td>
                      <td className="px-3 py-3 text-slate-600">{r.applicant || "–"}</td>
                      <td className="px-3 py-3 font-mono text-xs text-slate-600">{r.product_code || "–"}</td>
                      <td className="px-3 py-3 text-slate-600">
                        <span className={r.decision_description === "Substantially Equivalent" ? "text-emerald-400" : ""}>
                          {r.decision_description || "–"}
                        </span>
                        {r.decision_date && <div className="mt-0.5 text-[10px] text-slate-400">{r.decision_date}</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selected.size > 0 && (
        <div className="border border-slate-200 bg-white p-6">
          <div className="mb-5 flex items-center gap-2">
            <Scale className="h-4 w-4 text-[#2563EB]" />
            <div className="overline text-[#2563EB]">SUBSTANTIAL EQUIVALENCE ANALYSIS</div>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="overline mb-2 block text-slate-500">Subject device name</label>
              <input data-testid={PREDICATE.subjectInput} value={subject} onChange={(e) => setSubject(e.target.value)}
                     placeholder="e.g., CardioSense Pulse Ox v2"
                     className="w-full border border-slate-200 bg-[#FAFAF7] px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-300 focus:border-[#2563EB] focus:outline-none" />
            </div>
            <div>
              <label className="overline mb-2 block text-slate-500">Selected predicates</label>
              <div className="flex flex-wrap gap-2 border border-slate-200 bg-[#FAFAF7] px-3 py-2 min-h-[44px]">
                {Array.from(selected).map((k) => (
                  <span key={k} className="border border-[#2563EB]/40 bg-[#2563EB]/10 px-2 py-0.5 font-mono text-xs text-[#93C5FD]">{k}</span>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-4">
            <label className="overline mb-2 block text-slate-500">Subject device description</label>
            <textarea data-testid={PREDICATE.subjectDescInput} value={subjectDesc} onChange={(e) => setSubjectDesc(e.target.value)}
                      rows={3} placeholder="Technology, intended use, principle of operation, key features…"
                      className="w-full resize-none border border-slate-200 bg-[#FAFAF7] px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-300 focus:border-[#2563EB] focus:outline-none" />
          </div>
          <button data-testid={PREDICATE.runCompareBtn} disabled={analyzing} onClick={runCompare}
                  className="mt-5 inline-flex items-center gap-2 bg-[#2563EB] px-5 py-3 text-sm font-semibold text-white transition-all hover:bg-[#1D4ED8] disabled:opacity-50">
            {analyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Run AI Comparison
          </button>
          {(analysis || analyzing) && (
            <div className="mt-6 border-t border-slate-200 pt-6">
              {analysis ? <Markdown>{analysis}</Markdown> : (
                <div className="flex items-center gap-2 text-slate-500">
                  <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
                  <span className="ml-2 text-sm">Analyzing…</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
